import { NextPage } from "next"
import Script from "next/script"
import { useEffect, useRef, useState } from "react"
import { createGlobalStyle } from "styled-components"
import { team } from "../../../../etc/settings"
import { IPageWrapper } from "../../../../etc/types"
import DevNav from "../../../partials/DevNav"
import BlankPageWrapper from "../../blank-page-wrapper/views/BlankPageWrapper"

import styles from "../assets/css/index.module.scss"

// Overrides for the NBA header injected by embed.js: hides the utility nav's lg:block links
// and the language picker, shrinks the logo, and sets Praktika on the utility, primary-menu
// and drawer links. Matched on stable attributes, not NBA's hashed classes; !important beats Tailwind.
const GlobalStyle = createGlobalStyle`
	div[id="nba-nav"] nav[data-testid="utilty-nav"] ul[role="menubar"] > li[class~="lg:block"], nav[aria-label="Language Picker"] {
		display: none !important;
	}

	div[id="nba-nav"] nav[data-testid="utilty-nav"] ul[role="menubar"] > li {
		font-family: "Praktika Medium", Arial, Helvetica, sans-serif;
	}

	div[id="nba-nav"] nav[data-testid="utilty-nav"] a:first-child img {
		max-width: 2rem !important;

		@media (min-width: 640px) {
			max-width: 2.5rem !important;
		}
	}

	div[id="nba-nav"] ul[data-testid="drawer-links"] > li span[class="text-xl"],
	div[id="nba-nav"] nav[aria-label="header-primary-menu"] ul[role="menubar"] li[class="menu-item"] > button,
	nav[aria-label="header-primary-menu"] ul[role="menubar"] li[class="menu-item"] > a {
		font-family: "Praktika Extra Bold", Arial, Helvetica, sans-serif;
		font-size: 1.25rem;
		letter-spacing: 0.52px;
	}
`

const NBAPageWrapper: NextPage<IPageWrapper> = (props) => {
	const [showScrollTop, setShowScrollTop] = useState(false)
	const [satisfiButtonElement, setSatisfiButtonElement] = useState<HTMLElement>()
	const [satisfiClearance, setSatisfiClearance] = useState(0)
	const [isProd, setIsProd] = useState(true)
	const ref1 = useRef<HTMLButtonElement>(null)

	useEffect(() => {
		window.addEventListener("scroll", function () {
			if (ref1) {
				setShowScrollTop(500 < window.pageYOffset)
			}
		})

		// eslint-disable-next-line react-hooks/set-state-in-effect -- reads window.location on a statically-exported page; must run post-mount
		setIsProd(-1 === ["next-builds.grizzliesapp.com"].indexOf(window.location.hostname))
	}, [])

	useEffect(() => {
		// Use MutationObserver for efficient DOM monitoring
		const observer = new MutationObserver(() => {
			const observedElement = document.getElementById("divSatisfiChat")
			// Select the launcher by tag rather than child position -- Satisfi has reordered/added
			// sibling nodes (a badge, a whitespace text node, a hidden panel) around it before.
			// Scope to a direct child only: a nested match could be a button inside Satisfi's
			// hidden chat panel (send/close/minimize), which would report a 0x0 rect forever.
			const button = observedElement?.querySelector<HTMLElement>(":scope > button")

			// Require a visible, rendered match before stopping -- a hidden panel's button can
			// mount before the launcher does, and disconnecting on that match would be unrecoverable.
			if (button) {
				const rect = button.getBoundingClientRect()

				if (0 < rect.width && 0 < rect.height) {
					setSatisfiButtonElement(button)
					observer.disconnect() // Cleanup observer once a real, visible launcher is found
				}
			}
		})

		observer.observe(document.body, { childList: true, subtree: true })

		// Cleanup observer on unmount
		return () => observer.disconnect()
	}, [])

	useEffect(() => {
		if (!satisfiButtonElement) {
			return
		}

		// Measure the launcher's actual on-screen footprint instead of assuming a fixed height,
		// so this keeps clearing it if Satisfi resizes/repositions it again.
		const updateClearance = () => {
			const rect = satisfiButtonElement.getBoundingClientRect()

			setSatisfiClearance(0 < rect.width && 0 < rect.height ? window.innerHeight - rect.top : 0)
		}

		updateClearance()

		const resizeObserver = new ResizeObserver(updateClearance)

		resizeObserver.observe(satisfiButtonElement)
		window.addEventListener("resize", updateClearance)
		window.addEventListener("scroll", updateClearance, { passive: true })

		return () => {
			resizeObserver.disconnect()
			window.removeEventListener("resize", updateClearance)
			window.removeEventListener("scroll", updateClearance)
		}
	}, [satisfiButtonElement])

	return (
		<BlankPageWrapper {...props}>
			<GlobalStyle />
			<div id="nba-styles" data-team={team} />
			{isProd ?
				<div id="nba-nav" data-team={team} />
			:	<DevNav type="header" />}

			<div className={styles.NBAPageContent}>
				{props.children}
				<button
					ref={ref1}
					data-scroll-top
					className={[styles.scrollTop, showScrollTop && styles.in].filter(Boolean).join(" ")}
					style={
						0 < satisfiClearance ? { bottom: `calc(${satisfiClearance}px + 0.75rem)` } : undefined
					}
					onClick={() => window && window.scrollTo({ top: 0, behavior: "smooth" })}
				>
					Scroll to Top
				</button>
			</div>
			{isProd ?
				<>
					<div id="nba-footer" data-team={team} />
					<Script src="https://www.nba.com/_teams/include/embed.js" />
				</>
			:	<DevNav type="footer" />}
		</BlankPageWrapper>
	)
}

export default NBAPageWrapper
